import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type {
  Campo, Opcion, Pagina, RegistroCatalogo, ResumenCatalogo,
} from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Insignia, Paginacion,
} from '../componentes/comunes';

/**
 * Pantalla de los catálogos maestros.
 *
 * La API describe cada catálogo —sus campos, tipos y obligatoriedad— y
 * esta pantalla se arma con esa descripción. Agregar un catálogo nuevo
 * en el servidor lo hace aparecer aquí sin tocar el frontend.
 */
export default function Catalogos() {
  const { puede } = useSesion();

  const [catalogos, setCatalogos] = useState<ResumenCatalogo[]>([]);
  const [elegido, setElegido] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [cargandoIndice, setCargandoIndice] = useState(true);

  const cargarIndice = useCallback(() => {
    setCargandoIndice(true);
    api.get<ResumenCatalogo[]>('/catalogos')
      .then((r) => {
        setCatalogos(r);
        setElegido((actual) => actual ?? r[0]?.clave ?? null);
      })
      .catch(setError)
      .finally(() => setCargandoIndice(false));
  }, []);

  useEffect(cargarIndice, [cargarIndice]);

  const definicion = catalogos.find((c) => c.clave === elegido);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Catálogos</h1>
          <p className="sub">
            Datos maestros del sistema. Lo que se desactiva aquí deja de
            ofrecerse al capturar, pero no se borra: lo que ya lo usa sigue
            siendo válido.
          </p>
        </div>
      </div>

      <Error error={error} />

      {cargandoIndice ? <Cargando /> : (
        <>
          <div className="rejilla-catalogos">
            {catalogos.map((c) => (
              <button
                key={c.clave}
                className={`tarjeta ${c.clave === elegido ? 'activa' : ''}`}
                onClick={() => setElegido(c.clave)}
              >
                <div className="titulo">{c.etiqueta}</div>
                <div className="conteo">
                  {c.total} {c.total === 1 ? 'registro' : 'registros'}
                  {c.total !== c.activos && ` · ${c.total - c.activos} inactivos`}
                </div>
              </button>
            ))}
          </div>

          {definicion && (
            <TablaCatalogo
              definicion={definicion}
              puedeCrear={puede('catalogos', 'crear')}
              puedeEditar={puede('catalogos', 'editar')}
              alCambiar={cargarIndice}
            />
          )}
        </>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════ tabla

function TablaCatalogo({
  definicion, puedeCrear, puedeEditar, alCambiar,
}: {
  definicion: ResumenCatalogo;
  puedeCrear: boolean;
  puedeEditar: boolean;
  alCambiar: () => void;
}) {
  const [pagina, setPagina] = useState<Pagina<RegistroCatalogo> | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('');
  const [numero, setNumero] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [editando, setEditando] = useState<RegistroCatalogo | null>(null);
  const [creando, setCreando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState<RegistroCatalogo | null>(null);

  // Cambiar de catálogo tiene que reiniciar filtros y página, o se
  // consulta la página 7 de una tabla que sólo tiene dos.
  useEffect(() => {
    setBusqueda('');
    setEstado('');
    setNumero(1);
  }, [definicion.clave]);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<RegistroCatalogo>>(`/catalogos/${definicion.clave}`, {
      busqueda: busqueda || undefined,
      estado: estado || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [definicion.clave, busqueda, estado, numero]);

  // La búsqueda espera a que la persona deje de teclear: sin esto se
  // dispara una consulta por cada letra.
  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  const visibles = definicion.campos.filter((c) => c.nombre !== 'activo');

  return (
    <div className="panel">
      <div className="encabezado">
        <div>
          <h2>{definicion.etiqueta}</h2>
          <p className="sub">{definicion.descripcion}</p>
        </div>
        {puedeCrear && (
          <button className="primario" onClick={() => setCreando(true)}>
            Nuevo {definicion.singular.toLowerCase()}
          </button>
        )}
      </div>

      <div className="controles">
        <input
          type="search"
          placeholder={`Buscar en ${definicion.etiqueta.toLowerCase()}…`}
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
        />
        <select value={estado} onChange={(e) => { setEstado(e.target.value); setNumero(1); }}>
          <option value="">Todos</option>
          <option value="activo">Sólo activos</option>
          <option value="inactivo">Sólo inactivos</option>
        </select>
      </div>

      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">
              {busqueda ? 'No hay resultados para esa búsqueda.' : 'Todavía no hay registros.'}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  {visibles.map((c) => <th key={c.nombre}>{c.etiqueta}</th>)}
                  <th>Estado</th>
                  {puedeEditar && <th />}
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((fila) => (
                  <tr key={fila.id}>
                    {visibles.map((c) => (
                      <td key={c.nombre} className={c.tipo === 'clave' ? 'mono' : undefined}>
                        {celda(fila, c)}
                      </td>
                    ))}
                    <td><Insignia activo={fila.activo} /></td>
                    {puedeEditar && (
                      <td className="acciones">
                        <button className="enlace" onClick={() => setEditando(fila)}>Editar</button>
                        <button className="enlace" onClick={() => setCambiandoEstado(fila)}>
                          {fila.activo ? 'Desactivar' : 'Reactivar'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina}
            limite={pagina.limite}
            total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {(creando || editando) && (
        <FormularioCatalogo
          definicion={definicion}
          registro={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); alCambiar(); }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstado
          catalogo={definicion.clave}
          singular={definicion.singular}
          registro={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); alCambiar(); }}
        />
      )}
    </div>
  );
}

function celda(fila: RegistroCatalogo, campo: Campo): string {
  if (campo.tipo === 'referencia') {
    return String(fila[`${campo.nombre}_nombre`] ?? '—');
  }
  const valor = fila[campo.nombre];
  return valor === null || valor === undefined || valor === '' ? '—' : String(valor);
}

// ══════════════════════════════════════════════════════════ formulario

function FormularioCatalogo({
  definicion, registro, onCerrar, onGuardado,
}: {
  definicion: ResumenCatalogo;
  registro: RegistroCatalogo | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = registro === null;

  const [valores, setValores] = useState<Record<string, unknown>>(() => {
    const iniciales: Record<string, unknown> = {};
    for (const campo of definicion.campos) {
      iniciales[campo.nombre] = registro
        ? registro[campo.nombre] ?? ''
        : campo.nombre === 'activo' ? true : '';
    }
    return iniciales;
  });

  const [opciones, setOpciones] = useState<Record<string, Opcion[]>>({});
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  // Los desplegables de las referencias se piden al abrir el formulario.
  useEffect(() => {
    const referencias = definicion.campos.filter((c) => c.tipo === 'referencia' && c.refA);
    Promise.all(
      referencias.map((c) =>
        api.get<Opcion[]>(`/catalogos/${c.refA}/opciones`)
          .then((lista) => [c.nombre, lista] as const)
          .catch(() => [c.nombre, []] as const),
      ),
    ).then((pares) => setOpciones(Object.fromEntries(pares)));
  }, [definicion]);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      // En edición sólo se mandan los campos que cambiaron: así una
      // pantalla que no conozca una columna nueva no la borra al guardar.
      const datos: Record<string, unknown> = {};
      for (const campo of definicion.campos) {
        const valor = valores[campo.nombre];
        if (esAlta) {
          if (campo.tipo === 'clave' && valor === '') continue;   // se deriva del nombre
          if (valor !== '' || campo.requerido) datos[campo.nombre] = valor;
        } else {
          if (campo.inmutable) continue;
          const original = registro?.[campo.nombre] ?? '';
          if (String(valor ?? '') !== String(original ?? '')) datos[campo.nombre] = valor;
        }
      }

      if (esAlta) {
        await api.post(`/catalogos/${definicion.clave}`, { datos });
      } else if (Object.keys(datos).length > 0) {
        await api.patch(`/catalogos/${definicion.clave}/${registro!.id}`, { datos });
      }
      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? `Nuevo ${definicion.singular.toLowerCase()}` : `Editar ${definicion.singular.toLowerCase()}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando}>
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      {definicion.campos.map((campo) => {
        const deshabilitado = !esAlta && Boolean(campo.inmutable);
        const valor = valores[campo.nombre];

        if (campo.tipo === 'booleano') {
          return (
            <div className="campo inline" key={campo.nombre}>
              <input
                id={campo.nombre}
                type="checkbox"
                checked={valor === true}
                onChange={(e) => setValores({ ...valores, [campo.nombre]: e.target.checked })}
              />
              <label htmlFor={campo.nombre}>{campo.etiqueta}</label>
            </div>
          );
        }

        return (
          <div className="campo" key={campo.nombre}>
            <label htmlFor={campo.nombre}>
              {campo.etiqueta}{campo.requerido && ' *'}
            </label>

            {campo.tipo === 'referencia' ? (
              <select
                id={campo.nombre}
                value={String(valor ?? '')}
                disabled={deshabilitado}
                onChange={(e) => setValores({ ...valores, [campo.nombre]: e.target.value })}
              >
                <option value="">— sin asignar —</option>
                {(opciones[campo.nombre] ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{o.nombre}</option>
                ))}
              </select>
            ) : campo.tipo === 'texto_largo' ? (
              <textarea
                id={campo.nombre}
                rows={3}
                value={String(valor ?? '')}
                maxLength={campo.maximo}
                disabled={deshabilitado}
                onChange={(e) => setValores({ ...valores, [campo.nombre]: e.target.value })}
              />
            ) : (
              <input
                id={campo.nombre}
                type={campo.tipo === 'entero' || campo.tipo === 'decimal' ? 'number' : 'text'}
                value={String(valor ?? '')}
                maxLength={campo.tipo === 'texto' || campo.tipo === 'clave' ? campo.maximo : undefined}
                disabled={deshabilitado}
                className={campo.tipo === 'clave' ? 'mono' : undefined}
                onChange={(e) => setValores({ ...valores, [campo.nombre]: e.target.value })}
              />
            )}

            {campo.ayuda && <span className="ayuda">{campo.ayuda}</span>}
            {deshabilitado && <span className="ayuda">No se puede cambiar una vez creado.</span>}
          </div>
        );
      })}
    </Dialogo>
  );
}

// ═════════════════════════════════════════════════════ baja y alta

function CambioEstado({
  catalogo, singular, registro, onCerrar, onHecho,
}: {
  catalogo: string;
  singular: string;
  registro: RegistroCatalogo;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [uso, setUso] = useState<Record<string, number> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  const desactivando = registro.activo;

  // Antes de desactivar se consulta cuántos registros vivos dependen de
  // este, para que la decisión se tome con el dato a la vista.
  useEffect(() => {
    if (!desactivando) return;
    api.get<Record<string, number>>(`/catalogos/${catalogo}/${registro.id}/uso`)
      .then(setUso)
      .catch(() => setUso({}));
  }, [catalogo, registro.id, desactivando]);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = desactivando ? 'desactivar' : 'reactivar';
      await api.post(`/catalogos/${catalogo}/${registro.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  const enUso = Object.entries(uso ?? {});

  return (
    <DialogoMotivo
      titulo={`${desactivando ? 'Desactivar' : 'Reactivar'} ${singular.toLowerCase()}`}
      textoBoton={desactivando ? 'Desactivar' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <>
          <p>
            <strong>{String(registro.nombre ?? registro.clave ?? '')}</strong>
            {desactivando
              ? ' dejará de aparecer al capturar. No se borra nada.'
              : ' volverá a estar disponible.'}
          </p>
          {desactivando && enUso.length > 0 && (
            <div className="aviso" style={{ marginTop: 10 }}>
              Ahora mismo lo usan:
              <ul>
                {enUso.map(([que, cuantos]) => (
                  <li key={que}>{cuantos} {que}</li>
                ))}
              </ul>
              Esos registros no cambian.
            </div>
          )}
        </>
      }
    />
  );
}
